import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, History, Package, PackageOpen, RotateCcw, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const METHOD_LABELS = { POST: "Création", PUT: "Modification", PATCH: "Modification", DELETE: "Suppression" };

export default function BackupsManager() {
  const [backups, setBackups] = useState([]);
  const [logs, setLogs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = () => {
    api.get("/admin/backups").then((r) => setBackups(r.data)).catch(() => {});
    api.get("/admin/audit-log").then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const createManual = async () => {
    setCreating(true);
    try {
      await api.post("/admin/backups");
      toast.success("Sauvegarde manuelle créée.");
      load();
    } catch {
      toast.error("Erreur lors de la sauvegarde.");
    } finally {
      setCreating(false);
    }
  };

  const restore = async (backup) => {
    if (
      !window.confirm(
        `Restaurer la sauvegarde du ${backup.created_at.slice(0, 16).replace("T", " à ")} ?\n\nL'état actuel sera d'abord sauvegardé automatiquement, puis tous les contenus (carte, produits, prestations, réglages, sections, promotions…) seront remplacés par ceux de cette sauvegarde.`
      )
    ) {
      return;
    }
    setRestoring(backup.id);
    try {
      await api.post(`/admin/backups/${backup.id}/restore`);
      toast.success("Sauvegarde restaurée. Rechargez l'administration pour voir les contenus restaurés.");
      load();
    } catch {
      toast.error("Erreur lors de la restauration.");
    } finally {
      setRestoring(null);
    }
  };

  const download = async (backup) => {
    try {
      const r = await api.get(`/admin/backups/${backup.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sauvegarde-${backup.created_at.slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossible de télécharger la sauvegarde.");
    }
  };

  const exportTemplate = async () => {
    try {
      const r = await api.get("/admin/template/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `template-maasai-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template exporté (contenus, design, réglages — sans données clients ni secrets).");
    } catch {
      toast.error("Export du template impossible.");
    }
  };

  const importTemplate = async (file) => {
    if (!window.confirm(`Importer le template « ${file.name} » ?\n\nUne sauvegarde automatique de l'état actuel sera créée, puis les contenus et réglages seront remplacés par ceux du fichier.`)) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      await api.post("/admin/template/import", json);
      toast.success("Template importé. Rechargez l'administration pour voir les changements.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fichier de template invalide.");
    }
  };

  const fileRef = useRef(null);

  return (
    <div data-testid="backups-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ivory">Sauvegardes & historique</h2>
          <p className="mt-1 text-xs text-ivory/40">
            Une sauvegarde automatique est créée chaque jour (30 conservées). Vous pouvez restaurer
            ou télécharger n'importe quelle version.
          </p>
        </div>
        <button onClick={createManual} disabled={creating} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="backup-create-button">
          <Save className="h-4 w-4" strokeWidth={1.5} />
          {creating ? "Création…" : "Sauvegarder maintenant"}
        </button>
      </div>

      <div className="mb-10 flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-coal/60 p-5" data-testid="template-panel">
        <div className="min-w-60 flex-1">
          <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Template réutilisable</h3>
          <p className="mt-1 text-xs leading-relaxed text-ivory/40">
            Exportez toute la configuration (contenus, design, réglages) pour l'installer chez un
            nouveau client, ou importez un template existant.
          </p>
        </div>
        <button onClick={exportTemplate} className="btn-outline !h-10 !px-5 !text-[11px]" data-testid="template-export-button">
          <Package className="h-4 w-4" strokeWidth={1.5} />
          Exporter le template
        </button>
        <button onClick={() => fileRef.current?.click()} className="btn-outline !h-10 !px-5 !text-[11px]" data-testid="template-import-button">
          <PackageOpen className="h-4 w-4" strokeWidth={1.5} />
          Importer un template
        </button>
        <input ref={fileRef} type="file" accept="application/json" className="hidden" data-testid="template-import-input" onChange={(e) => { if (e.target.files[0]) importTemplate(e.target.files[0]); e.target.value = ""; }} />
      </div>

      {backups.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="backups-empty">Aucune sauvegarde pour le moment.</p>
      ) : (
        <div className="space-y-2" data-testid="backups-list">
          {backups.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`backup-row-${b.id}`}>
              <span className="text-sm text-ivory">
                {b.created_at.slice(0, 16).replace("T", " — ")}
              </span>
              <span className={`rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${b.type === "auto" ? "border-white/15 text-ivory/50" : "border-gold/40 text-gold"}`}>
                {b.type === "auto" ? "Automatique" : "Manuelle"}
              </span>
              <span className="text-xs text-ivory/40">
                {Object.entries(b.counts || {}).reduce((s, [, v]) => s + v, 0)} éléments
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => download(b)} className="flex h-8 items-center gap-2 rounded-md border border-white/10 px-3 text-xs text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`backup-download-${b.id}`}>
                  <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Télécharger
                </button>
                <button onClick={() => restore(b)} disabled={restoring === b.id} className="flex h-8 items-center gap-2 rounded-md border border-white/10 px-3 text-xs text-ivory/50 transition-colors hover:border-copper hover:text-copper" data-testid={`backup-restore-${b.id}`}>
                  <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {restoring === b.id ? "Restauration…" : "Restaurer"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-14">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          <History className="h-4 w-4" strokeWidth={1.5} />
          Journal des modifications
        </h3>
        {logs.length === 0 ? (
          <p className="text-sm text-ivory/40" data-testid="audit-empty">Aucune modification enregistrée pour le moment.</p>
        ) : (
          <div className="max-h-96 space-y-1.5 overflow-y-auto pr-2" data-testid="audit-list">
            {logs.map((log) => (
              <div key={log.id} className="flex items-center gap-4 rounded-md border border-white/5 px-4 py-2.5 text-xs" data-testid={`audit-row-${log.id}`}>
                <span className="shrink-0 text-ivory/35">{log.created_at.slice(0, 16).replace("T", " ")}</span>
                <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${log.method === "DELETE" ? "border-red-400/40 text-red-400" : log.method === "POST" ? "border-gold/40 text-gold" : "border-white/15 text-ivory/50"}`}>
                  {METHOD_LABELS[log.method] || log.method}
                </span>
                <span className="min-w-0 flex-1 truncate text-ivory/60">{log.path}</span>
                <span className="shrink-0 text-ivory/30">{log.user}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
